let observer;
let isLoading = false;
let productCache = new Map();
let currentPage = 1;
let hasNext = true;
let sectionId = "main-collection-product-grid";
let loadTimeout = null;

function getElements() {
  const root = document.getElementById("ProductGridContainer");
  const target = root?.querySelector(".pagination-wrapper");
  const grid = root?.querySelector("#product-grid");
  return { root, target, grid };
}

function createLoadingHTML() {
  return `<div class="use-tailwind"><div class="flex items-center justify-center p-8 text-gray-600 text-sm"><div class="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2"></div>Loading more products...</div></div>`;
}

function shouldRenderProduct(product) {
  const condition = product.getAttribute("data-condition");
  const inventory = parseInt(product.getAttribute("data-inventory") || "0");
  const isDraft = product.getAttribute("data-is-draft") === "true";
  const isReserved = product.getAttribute("data-is-reserved") === "true";
  const hasPartnersTag =
    product.getAttribute("data-partners-marketplace") === "true";
  const customerType = product.getAttribute("data-customer-type");

  if (isDraft || isReserved) return false;
  if (inventory <= 0) return false;
  if (hasPartnersTag && customerType === "Consumer") return false;
  if (["Mixed", "Salvage", "Phantom", ""].includes(condition)) return false;

  return true;
}

function extractSectionId() {
  const gridContainer = document.getElementById("ProductGridContainer");
  if (gridContainer) {
    const sectionElement = gridContainer.closest('[id^="shopify-section-"]');
    if (sectionElement) {
      const id = sectionElement.id.replace("shopify-section-", "");
      return id;
    }
  }

  const fallbackSection = document.querySelector("[data-id]");
  if (fallbackSection) {
    const id = fallbackSection.getAttribute("data-id");
    return id;
  }

  return sectionId;
}

function processResponse(responseText, isJson = false) {
  const { grid } = getElements();
  if (!grid) return;

  let htmlContent;

  if (isJson) {
    try {
      const data = JSON.parse(responseText);
      const sectionKey = Object.keys(data)[0];
      htmlContent = data[sectionKey];

      if (!htmlContent) {
        return;
      }
    } catch (error) {
      return;
    }
  } else {
    htmlContent = responseText;
  }

  const temp = document.createElement("div");
  temp.innerHTML = htmlContent;

  const newGrid = temp.querySelector("#product-grid");
  if (!newGrid) {
    return;
  }

  const pagination = temp.querySelector(".pagination-wrapper");
  hasNext = pagination?.hasAttribute("data-has-next") || false;

  const fragment = document.createDocumentFragment();
  Array.from(newGrid.children).forEach((product) => {
    if (product.tagName === "LI" && shouldRenderProduct(product)) {
      product.querySelectorAll("img[data-src]").forEach((img) => {
        if (!img.src) {
          img.src = img.dataset.src;
          img.removeAttribute("data-src");
        }
      });
      fragment.appendChild(product);
    }
  });

  grid.appendChild(fragment);
}

async function fetchPage(url, isJsonFallback = false) {
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    Accept: isJsonFallback ? "application/json" : "text/html",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.text();
}

function isTargetInRange(target) {
  const rect = target.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  return rect.top <= windowHeight + 2000;
}

function scheduleNextLoadIfInRange(target) {
  if (hasNext && isTargetInRange(target)) {
    setTimeout(() => {
      if (!isLoading && hasNext && isTargetInRange(target)) {
        loadPage();
      }
    }, 100);
  }
}

async function loadPage(pageNumber = null) {
  if (isLoading) {
    return;
  }

  const { target } = getElements();
  if (!target) {
    return;
  }

  if (pageNumber) currentPage = pageNumber;
  else currentPage++;

  console.info(`PLP: Loading page ${currentPage}`);
  const startTime = performance.now();

  if (!hasNext && !pageNumber) {
    const { grid } = getElements();
    observer?.unobserve(grid);
    target.innerHTML = "";
    return;
  }

  isLoading = true;
  target.innerHTML = createLoadingHTML();

  const params = new URLSearchParams(window.location.search);
  params.set("page", currentPage);

  const detectedSectionId = extractSectionId();
  const url = `${window.location.pathname}?${params}&section_id=${detectedSectionId}`;
  const cacheKey = url;

  if (productCache.has(cacheKey)) {
    processResponse(productCache.get(cacheKey), false);
    target.innerHTML = "";
    isLoading = false;

    scheduleNextLoadIfInRange(target);

    if (!hasNext) {
      const { grid } = getElements();
      observer?.unobserve(grid);
    }
    return;
  }

  try {
    const responseText = await fetchPage(url);

    /*
      TODO: Remove performance logging from here and move it to New Relic
      https://linear.app/secondshop/issue/SSC-159/move-performance-logging-from-plp-page-loading-to-new-relic
    */
    const endTime = performance.now();
    console.info(
      `PLP: Page ${currentPage} loaded in ${(endTime - startTime).toFixed(2)}ms`,
    );

    productCache.set(cacheKey, responseText);
    processResponse(responseText, false);
    target.innerHTML = "";

    scheduleNextLoadIfInRange(target);

    if (!hasNext) {
      const { grid } = getElements();
      observer?.unobserve(grid);
    }
  } catch (error) {
    try {
      const fallbackParams = new URLSearchParams(window.location.search);
      fallbackParams.set("page", currentPage);
      const fallbackUrl = `${window.location.pathname}?${fallbackParams}&sections=${detectedSectionId}`;

      target.innerHTML = createLoadingHTML();

      const responseText = await fetchPage(fallbackUrl, true);
      productCache.set(cacheKey, responseText);
      processResponse(responseText, true);
      target.innerHTML = "";

      scheduleNextLoadIfInRange(target);

      if (!hasNext) {
        const { grid } = getElements();
        observer?.unobserve(grid);
      }
    } catch (fallbackError) {
      target.innerHTML = "";
      isLoading = false;
    }
  } finally {
    if (target && target.innerHTML.includes("Loading more products")) {
      target.innerHTML = "";
    }
    isLoading = false;
  }
}

function createScrollObserver() {
  const { target, grid } = getElements();
  if (!target || !grid) return;

  const params = new URLSearchParams(window.location.search);
  currentPage = parseInt(params.get("page") || "1");
  hasNext = true;

  target.innerHTML = "";

  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (
        entry &&
        (entry.isIntersecting ||
          entry.intersectionRatio > 0 ||
          entry.boundingClientRect.bottom < 0) &&
        !isLoading &&
        hasNext
      ) {
        if (loadTimeout) clearTimeout(loadTimeout);
        loadTimeout = setTimeout(() => {
          if (!isLoading && hasNext) {
            loadPage();
          }
        }, 50);
      }
    },
    {
      rootMargin: "0px 0px 2000px 0px",
      threshold: [0, 0.1],
    },
  );

  observer.observe(target);
  target.innerHTML = "";
}

function preloadCriticalImages() {
  const images = document.querySelectorAll(
    '#product-grid img[src]:not([src=""])',
  );
  Array.from(images)
    .slice(0, 6)
    .forEach((img) => {
      if (img.src && !img.complete) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = img.src;
        document.head.appendChild(link);
      }
    });
}

function shouldInitialize() {
  return (
    window.location.href.includes("/collections") ||
    (window.location.href.includes("/search") &&
      !window.location.href.includes("/products"))
  );
}

document.addEventListener("DOMContentLoaded", () => {
  if (shouldInitialize()) {
    sectionId = extractSectionId();
    createScrollObserver();
    preloadCriticalImages();
  }
});

document.addEventListener("filters-applied", () => {
  if (shouldInitialize()) {
    productCache.clear();
    sectionId = extractSectionId();
    createScrollObserver();
  }
});
